import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, KeyRound, ShieldCheck, AlertTriangle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "cyl_pin_unlocked_until"; // ISO timestamp string
const SESSION_MS = 5 * 60 * 1000; // 5 minutes per user choice

// Helpers --------------------------------------------------------------------
const isUnlocked = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    return new Date(raw).getTime() > Date.now();
  } catch {
    return false;
  }
};

const markUnlocked = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, new Date(Date.now() + SESSION_MS).toISOString());
  } catch { /* noop */ }
};

const clearUnlock = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

// Exported helper for other components to know whether the PIN gate is open.
export const isPinUnlocked = isUnlocked;
export const lockPin = clearUnlock;

// PIN pad --------------------------------------------------------------------
const PinPad = ({ value, onChange, disabled, onSubmit, autoFocus = true, testIdPrefix = "pin" }) => {
  const inputs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputs.current[0]) inputs.current[0].focus();
  }, [autoFocus]);

  const setDigit = (i, v) => {
    const digit = String(v).replace(/[^0-9]/g, "").slice(-1);
    const next = value.split("");
    next[i] = digit;
    const merged = next.join("").padEnd(4, "").slice(0, 4);
    onChange(merged);
    if (digit && i < 3) inputs.current[i + 1]?.focus();
    if (digit && i === 3) {
      // submit on completing the 4th digit
      const fullPin = merged.slice(0, 3) + digit;
      if (fullPin.length === 4 && /^\d{4}$/.test(fullPin)) {
        // small defer so React state lands first
        setTimeout(() => onSubmit?.(fullPin), 0);
      }
    }
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !value[i] && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 3) inputs.current[i + 1]?.focus();
    if (e.key === "Enter" && /^\d{4}$/.test(value)) onSubmit?.(value);
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/[^0-9]/g, "").slice(0, 4);
    if (text.length === 4) {
      e.preventDefault();
      onChange(text);
      setTimeout(() => onSubmit?.(text), 0);
    }
  };

  return (
    <div className="flex justify-center gap-3" onPaste={onPaste}>
      {[0, 1, 2, 3].map((i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKey(i, e)}
          disabled={disabled}
          data-testid={`${testIdPrefix}-digit-${i}`}
          className="w-14 h-16 text-center text-3xl font-mono rounded-xl border-2 border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/40 outline-none disabled:opacity-50"
        />
      ))}
    </div>
  );
};

// Main gate ------------------------------------------------------------------
export const PinLockGate = ({ children, label = "Settings" }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState({ has_pin: null, locked_until: null });
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [mode, setMode] = useState("verify"); // verify | setup | confirm
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [lockMsg, setLockMsg] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const lockTimerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/pin/status`);
      setStatus(res.data);
      // Decide initial mode based on whether a PIN exists
      setMode(res.data.has_pin ? "verify" : "setup");
    } catch {
      // If the request fails (network), don't unlock — keep gate closed.
      setStatus({ has_pin: null, locked_until: null });
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount: read sessionStorage first, then fetch status.
  useEffect(() => {
    if (isAdmin) {
      setUnlocked(true);
      setLoading(false);
      return;
    }
    if (isUnlocked()) {
      setUnlocked(true);
      setLoading(false);
      // still fetch status so we have it cached if needed later
      fetchStatus();
    } else {
      fetchStatus();
    }
  }, [fetchStatus, isAdmin]);

  // Schedule a forced re-lock when the session expires.
  useEffect(() => {
    if (!unlocked) return;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const expiresAt = new Date(raw).getTime();
      const ms = Math.max(0, expiresAt - Date.now());
      lockTimerRef.current = setTimeout(() => {
        clearUnlock();
        setUnlocked(false);
        setPin("");
        setConfirmPin("");
        setMode(status.has_pin ? "verify" : "setup");
        toast.info("Settings locked after 5 minutes of access.");
      }, ms);
    } catch { /* noop */ }
    return () => {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    };
  }, [unlocked, status.has_pin]);

  // Countdown for lockout display.
  const lockedRemaining = useMemo(() => {
    if (!status.locked_until) return 0;
    try {
      return Math.max(0, Math.floor((new Date(status.locked_until).getTime() - Date.now()) / 1000));
    } catch { return 0; }
  }, [status.locked_until]);
  const [lockCountdown, setLockCountdown] = useState(lockedRemaining);
  useEffect(() => { setLockCountdown(lockedRemaining); }, [lockedRemaining]);
  useEffect(() => {
    if (lockCountdown <= 0) return;
    const t = setInterval(() => {
      setLockCountdown((c) => {
        if (c <= 1) {
          // refresh status so server-side flag clears
          fetchStatus();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [lockCountdown, fetchStatus]);

  const handleVerify = async (pinValue) => {
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/pin/verify`, { pin: pinValue });
      markUnlocked();
      setUnlocked(true);
      setPin("");
      toast.success(`${label} unlocked`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Incorrect PIN";
      if (err.response?.status === 423) {
        setLockMsg(detail);
        await fetchStatus();
      } else {
        setError(detail);
      }
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetup = async () => {
    if (submitting) return;
    setError("");
    if (!/^\d{4}$/.test(pin)) { setError("Enter a 4-digit PIN"); return; }
    if (pin !== confirmPin) { setError("PINs do not match"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/pin/set`, { new_pin: pin });
      toast.success("PIN created — Settings is now protected");
      markUnlocked();
      setUnlocked(true);
      setStatus((s) => ({ ...s, has_pin: true }));
      setPin("");
      setConfirmPin("");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save PIN");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async () => {
    if (forgotSubmitting) return;
    setForgotSubmitting(true);
    try {
      await axios.post(`${API}/auth/pin/forgot`, {});
      toast.success("Reset link sent — check your email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally {
      setForgotSubmitting(false);
    }
  };

  // Render ---------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (unlocked) {
    return <>{children}</>;
  }

  const locked = lockCountdown > 0;

  return (
    <Dialog open={true} onOpenChange={() => { /* cannot be closed */ }}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose={true}
        data-testid="pin-gate-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2">
            {mode === "setup" ? (
              <><KeyRound className="w-5 h-5 text-primary" /> Create your 4-digit PIN</>
            ) : (
              <><Lock className="w-5 h-5 text-primary" /> Enter PIN to open {label}</>
            )}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {mode === "setup"
              ? "This PIN protects Settings and the LVM (cash) report. You'll be asked for it each time the page is opened."
              : "Settings holds cash data and is locked. Enter your 4-digit PIN to continue."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {locked && (
            <div className="flex items-center gap-2 text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm font-mono" data-testid="pin-lockout-banner">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Too many wrong attempts. Try again in {lockCountdown}s.</span>
            </div>
          )}

          {mode === "verify" && (
            <>
              <PinPad
                value={pin}
                onChange={setPin}
                onSubmit={(v) => handleVerify(v)}
                disabled={submitting || locked}
                testIdPrefix="pin-verify"
              />
              {error && (
                <p className="text-center text-sm text-destructive font-mono" data-testid="pin-error">{error}</p>
              )}
              {lockMsg && !locked && (
                <p className="text-center text-xs text-amber-400 font-mono">{lockMsg}</p>
              )}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleForgot}
                  disabled={forgotSubmitting}
                  className="font-mono text-xs"
                  data-testid="pin-forgot-btn"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                  {forgotSubmitting ? "Sending reset link..." : "Forgot PIN? Email me a reset link"}
                </Button>
                <p className="text-center text-[11px] text-muted-foreground font-mono">
                  After 5 wrong attempts you&apos;ll be locked out for 60 seconds.
                </p>
              </div>
            </>
          )}

          {mode === "setup" && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-mono text-muted-foreground mb-2 text-center">Enter a 4-digit PIN</p>
                <PinPad
                  value={pin}
                  onChange={setPin}
                  onSubmit={() => { /* require confirm below */ }}
                  testIdPrefix="pin-setup"
                />
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground mb-2 text-center">Confirm PIN</p>
                <PinPad
                  value={confirmPin}
                  onChange={setConfirmPin}
                  autoFocus={false}
                  onSubmit={(v) => { setConfirmPin(v); handleSetup(); }}
                  testIdPrefix="pin-setup-confirm"
                />
              </div>
              {error && (
                <p className="text-center text-sm text-destructive font-mono" data-testid="pin-setup-error">{error}</p>
              )}
              <Button
                onClick={handleSetup}
                disabled={submitting || pin.length !== 4 || confirmPin.length !== 4}
                className="w-full btn-primary"
                data-testid="pin-setup-save"
              >
                <ShieldCheck className="w-4 h-4 mr-1.5" />
                {submitting ? "Saving..." : "Create PIN & Unlock"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PinLockGate;
